import { api, ApiError } from './client'

/**
 * F-074 (TASK-0328): campi dinamici della pratica.
 *
 * - `dynamicFieldsApi` e' il client di `DynamicFieldsController` (`/api/dynamic-fields`): definizione dei
 *   campi (solo Admin; l'elenco dei campi attivi e' per tutti).
 * - `contractFieldsApi` e' il client di `ContractDynamicFieldsController`
 *   (`/api/contracts/{id}/fields`): campi applicabili a UNA pratica, valori, verifica prima dell'invio.
 *
 * I campi di tipo Importo (e i calcolati che ne derivano) arrivano solo a chi ha `CanSeeRealAmounts`:
 * per gli altri la chiave e' ASSENTE dalla risposta.
 */
export type DynamicFieldType =
  | 'Testo'
  | 'Numero'
  | 'Importo'
  | 'Percentuale'
  | 'Data'
  | 'Casella'
  | 'Elenco'
  | 'ElencoMultiplo'
  | 'Email'
  | 'Telefono'
  | 'CodiceFiscale'
  | 'PartitaIva'
  | 'Calcolato'
  | 'Iban'
  | 'File'
  | 'Firma'

export const DYNAMIC_FIELD_TYPES: { value: DynamicFieldType; label: string }[] = [
  { value: 'Testo', label: 'Testo' },
  { value: 'Numero', label: 'Numero' },
  { value: 'Importo', label: 'Importo' },
  { value: 'Percentuale', label: 'Percentuale' },
  { value: 'Data', label: 'Data' },
  { value: 'Casella', label: 'Casella (si/no)' },
  { value: 'Elenco', label: 'Elenco (una scelta)' },
  { value: 'ElencoMultiplo', label: 'Elenco (piu\' scelte)' },
  { value: 'Email', label: 'Email' },
  { value: 'Telefono', label: 'Telefono' },
  { value: 'CodiceFiscale', label: 'Codice fiscale' },
  { value: 'PartitaIva', label: 'Partita IVA' },
  { value: 'Calcolato', label: 'Calcolato (formula)' },
  { value: 'Iban', label: 'IBAN' },
  { value: 'File', label: 'File (documento allegato)' },
  { value: 'Firma', label: 'Firma (tracciata a mano)' },
]

/**
 * F-108 (REQ-004-003): a cosa si riferisce una definizione. `Contract` = campo della pratica (default),
 * `Contact` = campo aggiuntivo del cliente. Immutabile dopo la creazione.
 */
export type DynamicFieldTarget = 'Contract' | 'Contact'

export const DYNAMIC_FIELD_TARGETS: { value: DynamicFieldTarget; label: string }[] = [
  { value: 'Contract', label: 'Pratica' },
  { value: 'Contact', label: 'Cliente' },
]

/** Tipi non ammessi per un campo del cliente (nessun importo sul cliente): il server risponde 400. */
export const CONTACT_FORBIDDEN_TYPES: DynamicFieldType[] = ['Importo', 'Calcolato', 'Firma']

export type DynamicFieldStatus = 'Bozza' | 'Attivo' | 'Archiviato'
export type ConditionOperator = 'eq' | 'ne' | 'gt' | 'lt' | 'contains'

export const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'eq', label: 'e\' uguale a' },
  { value: 'ne', label: 'e\' diverso da' },
  { value: 'gt', label: 'e\' maggiore di' },
  { value: 'lt', label: 'e\' minore di' },
  { value: 'contains', label: 'contiene' },
]

/** Attributi della pratica citabili in una condizione di visualizzazione. */
export const CONTRACT_ATTRIBUTES: { value: string; label: string }[] = [
  { value: 'department_id', label: 'Reparto (id)' },
  { value: 'operator_id', label: 'Operatore (id)' },
  { value: 'product_id', label: 'Prodotto (id)' },
  { value: 'customer_segment', label: 'Segmento cliente' },
  { value: 'contract_type', label: 'Tipo contratto' },
]

export interface DisplayCondition {
  field: string
  operator: ConditionOperator
  value: string
}

export interface DynamicFieldValidation {
  min?: number
  max?: number
  maxLength?: number
  decimals?: number
}

/**
 * F-098: da dove si precompila un campo. Elenco ordinato (al massimo 5): vince la prima origine che da' un valore.
 * `question` = risposta a una domanda dei questionari del call center; `customer` = attributo dell'anagrafica.
 */
export type PrefillSource =
  | { kind: 'question'; code: string }
  | { kind: 'customer'; attribute: string }

export const MAX_PREFILL_SOURCES = 5

/** Attributi dell'anagrafica ammessi come origine (gli stessi del server; niente IBAN). */
export const CUSTOMER_PREFILL_ATTRIBUTES: { value: string; label: string }[] = [
  { value: 'contact.first_name', label: 'Contatto: nome' },
  { value: 'contact.last_name', label: 'Contatto: cognome' },
  { value: 'contact.company_name', label: 'Contatto: ragione sociale' },
  { value: 'contact.email', label: 'Contatto: email' },
  { value: 'contact.phone', label: 'Contatto: telefono' },
  { value: 'contact.mobile', label: 'Contatto: cellulare' },
  { value: 'contact.fiscal_code', label: 'Contatto: codice fiscale' },
  { value: 'contact.vat_number', label: 'Contatto: partita IVA' },
  { value: 'contact.province', label: 'Contatto: provincia' },
  { value: 'person.address', label: 'Persona: indirizzo' },
  { value: 'person.city', label: 'Persona: citta\'' },
  { value: 'person.postal_code', label: 'Persona: CAP' },
  { value: 'person.province', label: 'Persona: provincia' },
  { value: 'person.fiscal_code', label: 'Persona: codice fiscale' },
  { value: 'person.email', label: 'Persona: email' },
  { value: 'person.phone', label: 'Persona: telefono' },
  { value: 'company.name', label: 'Azienda: ragione sociale' },
  { value: 'company.vat_number', label: 'Azienda: partita IVA' },
  { value: 'company.fiscal_code', label: 'Azienda: codice fiscale' },
  { value: 'company.pec', label: 'Azienda: PEC' },
  { value: 'company.sdi_code', label: 'Azienda: codice SDI' },
  { value: 'company.address', label: 'Azienda: indirizzo' },
  { value: 'company.city', label: 'Azienda: citta\'' },
  { value: 'company.postal_code', label: 'Azienda: CAP' },
  { value: 'company.province', label: 'Azienda: provincia' },
  { value: 'company.email', label: 'Azienda: email' },
  { value: 'company.phone', label: 'Azienda: telefono' },
]

export function prefillSourceLabel(s: PrefillSource): string {
  return s.kind === 'question'
    ? `Domanda: ${s.code}`
    : (CUSTOMER_PREFILL_ATTRIBUTES.find(a => a.value === s.attribute)?.label ?? s.attribute)
}

/** Una domanda dei questionari del call center, scelta come origine di un campo (solo Admin la puo' leggere). */
export interface PrefillQuestionChoice {
  code: string
  label: string
  formName: string
}

export interface DynamicFieldDefinition {
  id: number
  code: string
  label: string
  section: string | null
  sortOrder: number
  type: DynamicFieldType
  required: boolean
  departmentId: number | null
  operatorId: number | null
  productId: number | null
  customerSegment: string | null
  options: string[]
  validation: DynamicFieldValidation | null
  displayCondition: DisplayCondition[] | null
  formula: string | null
  /** Solo un'etichetta informativa: nessun mapping reale verso i portali (BLK-002). */
  externalMappingLabel: string | null
  /** F-098: origini del valore per la precompilazione; null = abbinamento per codice (come F-091). */
  prefillSources: PrefillSource[] | null
  /** R3-12: valore predefinito con cui parte il campo in una nuova pratica; null = nessuno. */
  defaultValue?: FieldValue | null
  version: number
  /** F-108: `Contract` (pratica) o `Contact` (cliente). */
  target: DynamicFieldTarget
  status: DynamicFieldStatus
  changeReason: string | null
  createdAt: string
  updatedAt: string | null
}

export interface DynamicFieldBody {
  code?: string
  /** F-108: solo alla creazione; poi immutabile. Assente = pratica. */
  target?: DynamicFieldTarget
  label: string
  type: DynamicFieldType
  required: boolean
  section?: string | null
  sortOrder?: number
  departmentId?: number | null
  operatorId?: number | null
  productId?: number | null
  customerSegment?: string | null
  options?: string[] | null
  validation?: DynamicFieldValidation | null
  displayCondition?: DisplayCondition[] | null
  formula?: string | null
  externalMappingLabel?: string | null
  prefillSources?: PrefillSource[] | null
  /** R3-12: valore predefinito (JSON del valore); null = nessuno. */
  defaultValue?: FieldValue | null
  changeReason?: string | null
}

export interface DynamicFieldScopeFilter {
  departmentId?: number
  operatorId?: number
  productId?: number
  customerSegment?: string
}

export const dynamicFieldsApi = {
  /** Campi Attivi; con un filtro d'ambito solo quelli applicabili a quella combinazione. */
  listActive: (scope?: DynamicFieldScopeFilter, target?: DynamicFieldTarget) => {
    const q = new URLSearchParams()
    if (target) q.set('target', target)
    if (scope?.departmentId !== undefined) q.set('departmentId', String(scope.departmentId))
    if (scope?.operatorId !== undefined) q.set('operatorId', String(scope.operatorId))
    if (scope?.productId !== undefined) q.set('productId', String(scope.productId))
    if (scope?.customerSegment) q.set('customerSegment', scope.customerSegment)
    const qs = q.toString()
    return api<{ items: DynamicFieldDefinition[] }>(`/api/dynamic-fields${qs ? `?${qs}` : ''}`).then(r => r.items)
  },
  /** Tutte le versioni e gli stati di una destinazione (solo Admin). Senza `target` valgono i campi della pratica. */
  listAll: (status?: DynamicFieldStatus, target: DynamicFieldTarget = 'Contract') => {
    const q = new URLSearchParams({ target })
    if (status) q.set('status', status)
    return api<{ items: DynamicFieldDefinition[] }>(`/api/dynamic-fields/all?${q.toString()}`).then(r => r.items)
  },
  /** R3-12: campi Attivi con ambito proprio su quel prodotto (obbligatori per primi). Con il flag spento il server risponde 404. */
  listForProduct: (productId: number) =>
    api<{ items: DynamicFieldDefinition[] }>(`/api/dynamic-fields/by-product/${productId}`).then(r => r.items),
  /** R3-12: validazione immediata di un valore, stesse regole del salvataggio; non scrive niente. */
  check: (code: string, value: FieldValue) =>
    api<{ valid: boolean; error: string | null }>(`/api/dynamic-fields/${encodeURIComponent(code)}/check`, {
      method: 'POST',
      body: JSON.stringify({ value }),
    }),
  create: (body: DynamicFieldBody) =>
    api<DynamicFieldDefinition>('/api/dynamic-fields', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: DynamicFieldBody) =>
    api<DynamicFieldDefinition>(`/api/dynamic-fields/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  activate: (id: number) => api<DynamicFieldDefinition>(`/api/dynamic-fields/${id}/activate`, { method: 'POST' }),
  archive: (id: number) => api<DynamicFieldDefinition>(`/api/dynamic-fields/${id}/archive`, { method: 'POST' }),
  /**
   * F-098: le domande di tutti i questionari (ultime versioni per codice), per scegliere l'origine di un campo.
   * Solo Admin: per gli altri la lettura e' negata e l'elenco resta vuoto.
   */
  prefillQuestions: async (): Promise<PrefillQuestionChoice[]> => {
    try {
      const forms = await api<
        { name: string; version: number; status: string; questions: { code: string; label: string }[] | null }[]
      >('/api/lead-qualification-forms')
      const byCode = new Map<string, PrefillQuestionChoice>()
      for (const f of [...forms].sort((a, b) => b.version - a.version)) {
        if (f.status === 'Archiviato') continue
        for (const q of f.questions ?? []) {
          if (!byCode.has(q.code)) byCode.set(q.code, { code: q.code, label: q.label, formName: f.name })
        }
      }
      return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code))
    } catch {
      return []
    }
  },
  newVersion: (id: number, reason: string) =>
    api<DynamicFieldDefinition>(`/api/dynamic-fields/${id}/new-version`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
}

// ---------------------------------------------------------------------------------------------
// Campi di una pratica
// ---------------------------------------------------------------------------------------------

/** Valore JSON di un campo: testo, numero, booleano, elenco di testi o nulla. */
export type FieldValue = string | number | boolean | string[] | FileFieldValue | null

/** F-155 (REQ-042-002): il valore di un campo File come lo scrive il server: id del documento e nome del file. */
export interface FileFieldValue {
  documentId: number
  fileName?: string
}

/** F-155: l'entita' (pratica o cliente) i cui allegati si possono scegliere in un campo File. */
export interface FileFieldRef {
  entityType: 'Contract' | 'Contact'
  entityId: number
}

/**
 * F-155: i campi File arrivano dal server come `{documentId, fileName}`; per l'editor diventano l'id come testo,
 * con il riferimento all'entita' (`fileRef`) da cui scegliere il documento. Il nome del file non si rimanda mai:
 * lo riscrive il server.
 */
function withFileRefs<T extends { fields: { type: DynamicFieldType; value: FieldValue; fileRef?: FileFieldRef }[] }>(
  res: T,
  ref: FileFieldRef,
): T {
  for (const f of res.fields) {
    if (f.type !== 'File') continue
    f.fileRef = ref
    const v = f.value
    f.value = v !== null && typeof v === 'object' && !Array.isArray(v) ? String(v.documentId) : v
  }
  return res
}

export interface ContractField {
  code: string
  label: string
  section: string | null
  sortOrder: number
  type: DynamicFieldType
  required: boolean
  options: string[]
  validation: DynamicFieldValidation | null
  displayCondition: DisplayCondition[] | null
  externalMappingLabel: string | null
  /** R3-12: solo nella nuova pratica (arriva dalla definizione): valore con cui parte il campo. */
  defaultValue?: FieldValue | null
  /** Versione della DEFINIZIONE in vigore. */
  version: number
  visible: boolean
  value: FieldValue
  /** Versione con cui il valore e' stato scritto (puo' essere piu' vecchia di `version`). */
  valueVersion: number | null
  /** F-155: solo per i campi File gia' salvati nella pratica: da dove scegliere il documento. */
  fileRef?: FileFieldRef
}

export interface ContractFields {
  contractId: number
  fields: ContractField[]
}

export interface SubmissionFinding {
  source: string
  code: string
  message: string
}

export interface SubmissionCheck {
  contractId: number
  canSubmit: boolean
  /** Se il server blocca davvero l'invio (`Contracts:Submission:EnforceChecks`). */
  enforced: boolean
  blocking: SubmissionFinding[]
  warnings: SubmissionFinding[]
}

export interface FieldError {
  code: string
  message: string
}

/** Gli attributi della pratica che decidono ambito e condizioni. */
export interface ContractFieldContext {
  departmentId?: number | null
  operatorId?: number | null
  productId?: number | null
  customerSegment?: string | null
  contractType?: string | null
}

export const contractFieldsApi = {
  get: (contractId: number) =>
    api<ContractFields>(`/api/contracts/${contractId}/fields`).then(r =>
      withFileRefs(r, { entityType: 'Contract', entityId: contractId })),
  save: (contractId: number, values: Record<string, FieldValue>) =>
    api<ContractFields>(`/api/contracts/${contractId}/fields`, { method: 'PUT', body: JSON.stringify({ values }) }).then(r =>
      withFileRefs(r, { entityType: 'Contract', entityId: contractId })),
  submissionCheck: (contractId: number) =>
    api<SubmissionCheck>(`/api/contracts/${contractId}/fields/submission-check`),
  /** F-091: anteprima del riporto delle risposte del questionario nei campi della pratica. Non scrive nulla. */
  prefillPreview: (contractId: number) =>
    api<PrefillPreview>(`/api/contracts/${contractId}/fields/prefill-from-lead`),
  /** F-091: riporta nei campi le risposte (tutte, o solo i `codes` indicati). Non sovrascrive mai un valore. */
  prefillApply: (contractId: number, codes?: string[]) =>
    api<PrefillResult>(`/api/contracts/${contractId}/fields/prefill-from-lead`, {
      method: 'POST',
      body: JSON.stringify(codes && codes.length > 0 ? { codes } : {}),
    }),
  /** F-098: anteprima del riporto dell'anagrafica nei campi della pratica. Non scrive nulla. */
  customerPrefillPreview: (contractId: number) =>
    api<CustomerPrefillPreview>(`/api/contracts/${contractId}/fields/prefill-from-customer`),
  /** F-098: riporta nei campi i valori dell'anagrafica (tutti, o solo i `codes`). Non sovrascrive mai un valore. */
  customerPrefillApply: (contractId: number, codes?: string[]) =>
    api<CustomerPrefillResult>(`/api/contracts/${contractId}/fields/prefill-from-customer`, {
      method: 'POST',
      body: JSON.stringify(codes && codes.length > 0 ? { codes } : {}),
    }),
}

// ---------------------------------------------------------------------------------------------
// F-108: campi aggiuntivi del cliente
// ---------------------------------------------------------------------------------------------

/** Un campo del cliente con visibilita' e valore. Nessun importo: i tipi Importo e Calcolato non esistono per il cliente. */
export interface ContactField {
  code: string
  label: string
  section: string | null
  sortOrder: number
  type: DynamicFieldType
  required: boolean
  options: string[]
  validation: DynamicFieldValidation | null
  displayCondition: DisplayCondition[] | null
  version: number
  visible: boolean
  value: FieldValue
  valueVersion: number | null
  /** F-155: solo per i campi File: da dove scegliere il documento. */
  fileRef?: FileFieldRef
}

export interface ContactFields {
  contactId: number
  /** True se il cliente e' nel cestino o e' stato fuso in un altro: si legge ma non si scrive. */
  readOnly: boolean
  readOnlyReason: string | null
  fields: ContactField[]
}

/** `ContactDynamicFieldsController` (`/api/contacts/{id}/fields`). */
export const contactFieldsApi = {
  get: (contactId: number) =>
    api<ContactFields>(`/api/contacts/${contactId}/fields`).then(r =>
      withFileRefs(r, { entityType: 'Contact', entityId: contactId })),
  save: (contactId: number, values: Record<string, FieldValue>) =>
    api<ContactFields>(`/api/contacts/${contactId}/fields`, { method: 'PUT', body: JSON.stringify({ values }) }).then(r =>
      withFileRefs(r, { entityType: 'Contact', entityId: contactId })),
}

/** F-098: una riga del riporto "dall'anagrafica ai campi della pratica". */
export interface CustomerPrefillItem {
  fieldCode: string
  fieldLabel: string
  /** L'attributo dell'anagrafica usato, es. `contact.fiscal_code`. */
  attribute: string
  attributeLabel: string
  apply: boolean
  skipReason: string | null
  value: string | null
}

export interface CustomerPrefillPreview {
  contractId: number
  canApply: boolean
  /** Anagrafica assente, nel cestino o fusa: l'elenco e' vuoto e qui c'e' il motivo. */
  emptyReason: string | null
  items: CustomerPrefillItem[]
}

export interface CustomerPrefillResult {
  contractId: number
  written: string[]
  emptyReason: string | null
  items: CustomerPrefillItem[]
}

/** F-091: una riga del riporto "dal questionario ai campi della pratica". */
export interface PrefillItem {
  fieldCode: string
  fieldLabel: string
  questionLabel: string
  leadId: number
  /** true = verrebbe scritto (anteprima) / e' stato scritto (esito). */
  apply: boolean
  /** Perche' si salta (es. "già presente"). */
  skipReason: string | null
  /** Il valore risposto, solo per le righe applicabili. */
  value: string | null
}

export interface PrefillPreview {
  contractId: number
  /** false se la pratica non e' piu' in uno stato in cui i campi si possono scrivere. */
  canApply: boolean
  leadIds: number[]
  items: PrefillItem[]
}

export interface PrefillResult {
  contractId: number
  written: string[]
  items: PrefillItem[]
}

/** Evento `window` lanciato quando i campi della pratica cambiano fuori da `ContractDynamicFields`. */
export const CONTRACT_FIELDS_CHANGED_EVENT = 'contract-fields-changed'

/** Esito dell'invio al backoffice: `undefined` (204) oppure gli avvisi (200) a controllo non imposto. */
export interface SubmitResult {
  enforced: boolean
  warnings: SubmissionFinding[]
  wouldHaveBlocked: SubmissionFinding[]
}

export interface SubmitBlocked {
  error: string
  blocking: SubmissionFinding[]
  warnings: SubmissionFinding[]
}

/**
 * Cambio di stato verso `InviataAlBackoffice`. A controllo imposto i blocchi arrivano come 422: l'errore
 * porta `blocking` e `warnings` (vedi `submitBlockedFrom`). Gli altri cambi di stato non passano di qui.
 */
export function submitToBackoffice(
  contractId: number, reason?: string, asExternalAgent = false,
): Promise<SubmitResult | undefined> {
  // F-135: EqualAgente, EqualAgenzia ed EqualSegnalatore non hanno la policy di PATCH /status (EqualStaff):
  // inviano dall'endpoint dedicato, che accetta solo questa transizione e solo sulle loro pratiche.
  if (asExternalAgent) {
    return api<SubmitResult | undefined>(`/api/contracts/${contractId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }
  return api<SubmitResult | undefined>(`/api/contracts/${contractId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ newStatus: 'InviataAlBackoffice', reason }),
  })
}

/** Se l'errore e' il 422 della verifica d'invio, ne restituisce blocchi e avvisi. */
export function submitBlockedFrom(err: unknown): SubmitBlocked | null {
  if (!(err instanceof ApiError)) return null
  const b = err.body as Partial<SubmitBlocked> | undefined
  if (!b || !Array.isArray(b.blocking)) return null
  return {
    error: typeof b.error === 'string' ? b.error : err.message,
    blocking: b.blocking,
    warnings: Array.isArray(b.warnings) ? b.warnings : [],
  }
}

/** Gli errori per campo di un 400 del salvataggio dei valori, se presenti. */
export function fieldErrorsFrom(err: unknown): FieldError[] {
  if (!(err instanceof ApiError)) return []
  const b = err.body as { errors?: unknown } | undefined
  if (!b || !Array.isArray(b.errors)) return []
  return b.errors.filter(
    (e): e is FieldError =>
      typeof e === 'object' && e !== null && typeof (e as FieldError).code === 'string' && typeof (e as FieldError).message === 'string',
  )
}

// ---------------------------------------------------------------------------------------------
// Condizioni di visualizzazione, lato client (anteprima live)
// ---------------------------------------------------------------------------------------------

/**
 * Testo di un valore come lo vede il motore delle condizioni del server: numeri con il punto, Casella
 * "true"/"false", elenco multiplo con le voci separate da virgola. Vuoto -> `null` (attributo assente).
 */
export function conditionText(value: FieldValue | undefined): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value)) return value.length === 0 ? null : value.join(',')
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') return String(value.documentId) // F-155: campo File
  const s = value.trim()
  if (s === '') return null
  // Un numero scritto all'italiana ("12,5") vale 12.5, come lo normalizza il server.
  return /^-?\d+,\d+$/.test(s) ? s.replace(',', '.') : s
}

function matches(c: DisplayCondition, actual: string): boolean {
  switch (c.operator) {
    case 'eq':
      return actual === c.value
    case 'ne':
      return actual !== c.value
    case 'gt': {
      const a = Number(actual)
      const b = Number(c.value)
      return Number.isFinite(a) && Number.isFinite(b) && a > b
    }
    case 'lt': {
      const a = Number(actual)
      const b = Number(c.value)
      return Number.isFinite(a) && Number.isFinite(b) && a < b
    }
    case 'contains':
      return actual.toLowerCase().includes(c.value.toLowerCase())
    default:
      return false
  }
}

/** Come `ConditionMatcher` del server: tutte le condizioni devono valere; un attributo assente fa fallire. */
export function evaluateConditions(conditions: DisplayCondition[] | null, bag: Record<string, string>): boolean {
  if (!conditions || conditions.length === 0) return true
  return conditions.every(c => {
    const actual = bag[c.field]
    return actual !== undefined && matches(c, actual)
  })
}

/** Gli attributi della pratica come testo, senza i vuoti (come il server). */
export function contractAttributeBag(ctx: ContractFieldContext): Record<string, string> {
  const bag: Record<string, string> = {}
  if (ctx.departmentId != null) bag['department_id'] = String(ctx.departmentId)
  if (ctx.operatorId != null) bag['operator_id'] = String(ctx.operatorId)
  if (ctx.productId != null) bag['product_id'] = String(ctx.productId)
  if (ctx.customerSegment) bag['customer_segment'] = ctx.customerSegment
  if (ctx.contractType) bag['contract_type'] = ctx.contractType
  return bag
}

/**
 * Visibilita' dei campi dato il contesto e i valori correnti. Per passate, fino a stabilizzarsi: un campo
 * nascosto non alimenta le condizioni degli altri (stessa regola del server).
 */
export function resolveVisibility(
  fields: Pick<ContractField, 'code' | 'displayCondition'>[],
  ctx: ContractFieldContext,
  values: Record<string, FieldValue>,
): Record<string, boolean> {
  const attrs = contractAttributeBag(ctx)
  const visible: Record<string, boolean> = {}
  for (const f of fields) visible[f.code] = true
  for (let pass = 0; pass <= fields.length + 1; pass++) {
    const bag: Record<string, string> = { ...attrs }
    for (const f of fields) {
      if (!visible[f.code]) continue
      const text = conditionText(values[f.code])
      if (text !== null) bag[f.code] = text
    }
    let changed = false
    for (const f of fields) {
      const now = evaluateConditions(f.displayCondition, bag)
      if (visible[f.code] !== now) {
        visible[f.code] = now
        changed = true
      }
    }
    if (!changed) break
  }
  return visible
}
